import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useOfflineGuard } from '@/hooks/useOfflineGuard';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

// ========== TYPES ==========
type NiveauUrgence = 'urgent' | 'prioritaire' | 'normal';
type SalleType = 'chef' | 'garde';
type TabVue = 'accueil' | 'affichage' | 'medecin' | 'medias';

interface PatientFile {
  id: string;
  salle: SalleType;
  ticket_seq: number;
  ticket_number: string;
  patient_id: string | null;
  numero_dossier: string;
  niveau_urgence: NiveauUrgence;
  statut: 'en_attente' | 'en_cours';
  created_at: string;
}

interface PatientInfo {
  id: string;
  nom: string;
  prenom: string;
  numero_dossier: string;
  telephone?: string;
}

interface VideoSalleAttente {
  id: string;
  titre: string;
  storage_path: string;
  actif: boolean;
  ordre: number;
  created_at: string;
}

// ========== CONSTANTES ==========
const DUREE_CONSULTATION = 12;
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100 MB

const URGENCE_CONFIG: Record<NiveauUrgence, { label: string; icon: string; prefix: string; border: string; badge: string; text: string; bg: string }> = {
  urgent: { label: 'Urgent', icon: 'ri-alert-fill', prefix: 'U', border: 'border-red-400', badge: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' },
  prioritaire: { label: 'Prioritaire', icon: 'ri-arrow-up-circle-fill', prefix: 'P', border: 'border-amber-400', badge: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' },
  normal: { label: 'Normal', icon: 'ri-check-line', prefix: 'N', border: 'border-clinic-blue/30', badge: 'bg-clinic-blue', text: 'text-clinic-blue-dark', bg: 'bg-clinic-blue/5' },
};

const SALLE_CONFIG: Record<SalleType, { label: string; ticketPrefix: string; color: string; bgLight: string; borderColor: string }> = {
  chef: { label: 'Dr OLYMPIO Camille', ticketPrefix: 'C', color: 'text-clinic-green', bgLight: 'bg-clinic-green/5', borderColor: 'border-clinic-green/40' },
  garde: { label: 'Médecins de Garde', ticketPrefix: 'G', color: 'text-clinic-blue', bgLight: 'bg-clinic-blue/5', borderColor: 'border-clinic-blue/40' },
};

const URGENCE_ORDER: NiveauUrgence[] = ['urgent', 'prioritaire', 'normal'];

// ========== HOOK: File d'attente temps réel ==========
function useFileAttenteRealtime(salleFilter?: SalleType) {
  const [files, setFiles] = useState<PatientFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const retryCount = useRef(0);
  const maxRetries = 3;
  const retryDelay = useRef(2000);
  const mountedRef = useRef(false);

  const loadFiles = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
      }
      let query = supabase.from('file_attente').select('*').order('salle', { ascending: true }).order('ticket_seq', { ascending: true });
      if (salleFilter) query = query.eq('salle', salleFilter);
      const { data, error: err } = await query;
      if (err) throw err;
      if (!mountedRef.current) return;
      setFiles((data || []) as PatientFile[]);
      setError('');
      retryCount.current = 0;
      retryDelay.current = 2000;
    } catch (e: any) {
      if (!mountedRef.current) return;
      console.error('Erreur chargement file:', e);
      const errCode = e?.code || '';
      const errDetails = e?.details || '';
      const errHint = e?.hint || '';
      const errMessage = e?.message || '';
      console.error('Détails erreur Supabase:', { code: errCode, details: errDetails, hint: errHint, message: errMessage });
      
      const isNetworkError = errMessage === 'TypeError: Failed to fetch' || errMessage?.includes('Failed to fetch') || errMessage?.includes('NetworkError') || errCode === 'PGRST301';

      if (isNetworkError && retryCount.current < maxRetries) {
        retryCount.current += 1;
        const delay = retryDelay.current * retryCount.current;
        console.warn(`Tentative ${retryCount.current}/${maxRetries} dans ${delay / 1000}s...`);
        setTimeout(() => loadFiles(true), delay);
        return;
      }

      if (isNetworkError) {
        setError('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
      } else {
        const errorParts = [];
        if (errCode) errorParts.push(`Code: ${errCode}`);
        if (errMessage && errMessage !== '' && errMessage !== '{"message":""}') errorParts.push(errMessage);
        if (errDetails) errorParts.push(errDetails);
        if (errHint) errorParts.push(errHint);
        setError(`Erreur de chargement: ${errorParts.length > 0 ? errorParts.join(' — ') : 'Erreur inconnue'}`);
      }
    } finally {
      if (mountedRef.current && (!isRetry || retryCount.current >= maxRetries)) {
        setLoading(false);
      }
    }
  }, [salleFilter]);

  useEffect(() => {
    mountedRef.current = true;
    retryCount.current = 0;
    loadFiles();

    // Polling de secours toutes les 8s au cas où le realtime échoue
    const pollingInterval = setInterval(() => {
      loadFiles(true);
    }, 8000);

    const channelName = `file_attente_rt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'file_attente' }, () => {
        loadFiles(true);
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('Realtime channel error, reloading data...');
          loadFiles(true);
        }
      });

    return () => {
      mountedRef.current = false;
      clearInterval(pollingInterval);
      supabase.removeChannel(channel);
    };
  }, [loadFiles]);

  const ajouterPatient = useCallback(async (
    salle: SalleType,
    patientInfo: PatientInfo | null,
    niveau: NiveauUrgence,
  ): Promise<boolean> => {
    try {
      // Générer un numéro de ticket aléatoire à 3 chiffres (100-999)
      let ticketNumber: string;
      let attempts = 0;
      const maxAttempts = 50;
      // Plage large : 100 à 9999 pour encore moins de risque de doublon visuel
      while (attempts < maxAttempts) {
        const randomNum = Math.floor(Math.random() * 9000) + 1000;
        ticketNumber = String(randomNum);
        const { data: existing } = await supabase
          .from('file_attente')
          .select('id')
          .eq('ticket_number', ticketNumber)
          .maybeSingle();
        if (!existing) break;
        attempts++;
      }
      if (attempts >= maxAttempts) {
        // Fallback : numéro aléatoire avec timestamp pour garantir l'unicité
        const ts = Date.now() % 9000 + 1000;
        ticketNumber = String(ts);
      }

      const { data: maxData } = await supabase
        .from('file_attente')
        .select('ticket_seq')
        .eq('salle', salle)
        .order('ticket_seq', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextSeq = (maxData?.ticket_seq || 0) + 1;

      const { error: insertErr } = await supabase.from('file_attente').insert({
        salle,
        ticket_seq: nextSeq,
        ticket_number: ticketNumber!,
        patient_id: patientInfo?.id || null,
        numero_dossier: patientInfo?.numero_dossier || '',
        niveau_urgence: niveau,
        statut: 'en_attente',
      });

      if (insertErr) throw insertErr;
      return true;
    } catch (e: any) {
      console.error('Erreur ajout patient:', e);
      return false;
    }
  }, []);

  const appelerSuivant = useCallback(async (salle: SalleType) => {
    try {
      // 1. Supprimer le patient actuellement "en_cours" pour cette salle
      const { data: enCours } = await supabase
        .from('file_attente')
        .select('id')
        .eq('salle', salle)
        .eq('statut', 'en_cours')
        .maybeSingle();

      if (enCours) {
        const { error: delErr } = await supabase.from('file_attente').delete().eq('id', enCours.id);
        if (delErr) console.error('[appelerSuivant] Erreur suppression en_cours:', delErr);
      }

      // 2. Récupérer TOUS les patients en attente depuis la DB (pas le state local !)
      const { data: tousEnAttente, error: fetchErr } = await supabase
        .from('file_attente')
        .select('*')
        .eq('salle', salle)
        .eq('statut', 'en_attente');

      if (fetchErr) {
        console.error('[appelerSuivant] Erreur récupération file:', fetchErr);
        throw fetchErr;
      }

      // 3. Trier par urgence puis ticket_seq (côté client car DB ne peut pas trier par ordre custom)
      const sorted = ((tousEnAttente || []) as PatientFile[]).sort((a, b) => {
        const ia = URGENCE_ORDER.indexOf(a.niveau_urgence);
        const ib = URGENCE_ORDER.indexOf(b.niveau_urgence);
        if (ia !== ib) return ia - ib;
        return a.ticket_seq - b.ticket_seq;
      });

      // 4. Passer le premier au statut "en_cours"
      if (sorted.length > 0) {
        const now = new Date().toISOString();
        const { error: updateErr } = await supabase
          .from('file_attente')
          .update({ statut: 'en_cours', updated_at: now })
          .eq('id', sorted[0].id);

        if (updateErr) {
          console.error('[appelerSuivant] Erreur update patient:', updateErr);
          throw updateErr;
        }

        // 5. Forcer un rafraîchissement local immédiat
        loadFiles(true);

        return sorted[0].ticket_number;
      }
      return null;
    } catch (e: any) {
      console.error('[appelerSuivant] Erreur:', e);
      return null;
    }
  }, [loadFiles]);

  const retirerPatient = useCallback(async (id: string) => {
    try {
      await supabase.from('file_attente').delete().eq('id', id);
    } catch (e) {
      console.error('Erreur retrait:', e);
    }
  }, []);

  const terminerConsultation = useCallback(async (salle: SalleType): Promise<boolean> => {
    try {
      const { data: enCours } = await supabase
        .from('file_attente')
        .select('id')
        .eq('salle', salle)
        .eq('statut', 'en_cours')
        .maybeSingle();

      if (enCours) {
        const { error: delErr } = await supabase.from('file_attente').delete().eq('id', enCours.id);
        if (delErr) throw delErr;
      }

      loadFiles(true);
      return true;
    } catch (e: any) {
      console.error('[terminerConsultation] Erreur:', e);
      return false;
    }
  }, [loadFiles]);

  const getPatientEnCours = useCallback((salle: SalleType): PatientFile | null => {
    return files.find(f => f.salle === salle && f.statut === 'en_cours') || null;
  }, [files]);

  const getFilesEnAttente = useCallback((salle?: SalleType): PatientFile[] => {
    const filtered = salle ? files.filter(f => f.salle === salle) : files;
    return filtered.filter(f => f.statut === 'en_attente');
  }, [files]);

  return { files, loading, error, loadFiles, retryLoad: () => loadFiles(), ajouterPatient, appelerSuivant, retirerPatient, terminerConsultation, getPatientEnCours, getFilesEnAttente };
}

// ========== SON ==========
function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playBeep = (freq: number, duration: number, startTime: number, vol: number = 0.15) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(vol, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playBeep(880, 0.15, now);
    playBeep(1100, 0.2, now + 0.15);
    playBeep(1320, 0.3, now + 0.35);
    setTimeout(() => ctx.close(), 1000);
  } catch (_) { /* audio non supporté */ }
}

// ========== HOOK: Vidéos salle d'attente ==========
function useVideosSalleAttente() {
  const [videos, setVideos] = useState<VideoSalleAttente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const retryCount = useRef(0);
  const maxRetries = 3;
  const retryDelay = useRef(2000);

  const loadVideos = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) setLoading(true);
      const { data, error: err } = await supabase
        .from('videos_salle_attente')
        .select('*')
        .order('ordre', { ascending: true });
      if (err) throw err;
      setVideos((data || []) as VideoSalleAttente[]);
      setError('');
      retryCount.current = 0;
      retryDelay.current = 2000;
    } catch (e: any) {
      console.error('Erreur chargement vidéos:', e);
      const errCode = e?.code || '';
      const errDetails = e?.details || '';
      const errHint = e?.hint || '';
      const errMessage = e?.message || '';
      console.error('Détails erreur Supabase (vidéos):', { code: errCode, details: errDetails, hint: errHint, message: errMessage });
      
      const isNetworkError = errMessage === 'TypeError: Failed to fetch' || errMessage?.includes('Failed to fetch') || errMessage?.includes('NetworkError') || errCode === 'PGRST301';

      if (isNetworkError && retryCount.current < maxRetries) {
        retryCount.current += 1;
        const delay = retryDelay.current * retryCount.current;
        setTimeout(() => loadVideos(true), delay);
        return;
      }

      if (isNetworkError) {
        setError('Impossible de se connecter au serveur pour charger les vidéos.');
      } else {
        const errorParts = [];
        if (errCode) errorParts.push(`Code: ${errCode}`);
        if (errMessage && errMessage !== '' && errMessage !== '{"message":""}') errorParts.push(errMessage);
        if (errDetails) errorParts.push(errDetails);
        if (errHint) errorParts.push(errHint);
        setError(`Erreur vidéos: ${errorParts.length > 0 ? errorParts.join(' — ') : 'Erreur inconnue'}`);
      }
    } finally {
      if (!isRetry || retryCount.current >= maxRetries) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    retryCount.current = 0;
    loadVideos();
  }, [loadVideos]);

  const uploadVideo = useCallback(async (file: File, titre: string): Promise<boolean> => {
    try {
      if (file.size > MAX_VIDEO_SIZE) {
        setError('La vidéo dépasse la limite de 100 Mo');
        return false;
      }

      const ext = file.name.split('.').pop()?.toLowerCase() || 'mp4';
      const safeName = `videos/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from('public')
        .upload(safeName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadErr) {
        if (uploadErr.message?.includes('bucket') || uploadErr.message?.includes('not found')) {
          setError('Le bucket de stockage "public" n\'existe pas. Veuillez le créer dans le dashboard Supabase (Storage > New Bucket > nom: public, public: ON).');
        } else {
          setError(`Erreur upload: ${uploadErr.message}`);
        }
        return false;
      }

      const { data: maxOrdre } = await supabase
        .from('videos_salle_attente')
        .select('ordre')
        .order('ordre', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextOrdre = (maxOrdre?.ordre || 0) + 1;

      const { error: insertErr } = await supabase
        .from('videos_salle_attente')
        .insert({
          titre: titre || file.name,
          storage_path: safeName,
          actif: true,
          ordre: nextOrdre,
        });

      if (insertErr) throw insertErr;

      await loadVideos();
      return true;
    } catch (e: any) {
      console.error('Erreur upload vidéo:', e);
      setError(e.message || 'Erreur lors de l\'upload');
      return false;
    }
  }, [loadVideos]);

  const toggleActif = useCallback(async (id: string, actif: boolean) => {
    try {
      await supabase.from('videos_salle_attente').update({ actif }).eq('id', id);
      await loadVideos();
    } catch (e: any) {
      console.error('Erreur toggle vidéo:', e);
    }
  }, [loadVideos]);

  const deleteVideo = useCallback(async (video: VideoSalleAttente) => {
    try {
      const { error: storageErr } = await supabase.storage
        .from('public')
        .remove([video.storage_path]);
      if (storageErr) console.warn('Erreur suppression fichier:', storageErr);

      await supabase.from('videos_salle_attente').delete().eq('id', video.id);
      await loadVideos();
    } catch (e: any) {
      console.error('Erreur suppression vidéo:', e);
    }
  }, [loadVideos]);

  const moveVideo = useCallback(async (id: string, direction: 'up' | 'down', allVideos: VideoSalleAttente[]) => {
    const sorted = [...allVideos].sort((a, b) => a.ordre - b.ordre);
    const idx = sorted.findIndex(v => v.id === id);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const current = sorted[idx];
    const swap = sorted[swapIdx];

    try {
      await supabase.from('videos_salle_attente').update({ ordre: swap.ordre }).eq('id', current.id);
      await supabase.from('videos_salle_attente').update({ ordre: current.ordre }).eq('id', swap.id);
      await loadVideos();
    } catch (e: any) {
      console.error('Erreur réordre vidéo:', e);
    }
  }, [loadVideos]);

  const getPublicUrl = useCallback((storagePath: string): string => {
    const { data } = supabase.storage.from('public').getPublicUrl(storagePath);
    return data.publicUrl;
  }, []);

  return { videos, loading, error, loadVideos, uploadVideo, toggleActif, deleteVideo, moveVideo, getPublicUrl };
}

// ========== COMPOSANT: Lecteur Vidéo ==========
function VideoPlayer({ videos, getPublicUrl }: {
  videos: VideoSalleAttente[];
  getPublicUrl: (path: string) => string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const activeVideos = videos
    .filter(v => v.actif)
    .sort((a, b) => a.ordre - b.ordre);

  const currentVideo = activeVideos[currentIdx];

  const handleEnded = useCallback(() => {
    if (activeVideos.length <= 1) {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
      return;
    }
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIdx((prev) => (prev + 1) % activeVideos.length);
      setIsTransitioning(false);
    }, 400);
  }, [activeVideos.length]);

  const handleError = useCallback(() => {
    if (activeVideos.length <= 1) return;
    setCurrentIdx((prev) => (prev + 1) % activeVideos.length);
  }, [activeVideos.length]);

  useEffect(() => {
    if (videoRef.current && currentVideo) {
      videoRef.current.load();
      const playPromise = videoRef.current.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Autoplay blocked — no action needed, user may click
        });
      }
    }
  }, [currentIdx, currentVideo]);

  // Reset to first video when activeVideos changes
  useEffect(() => {
    setCurrentIdx(0);
  }, [activeVideos.length]);

  if (activeVideos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-white/60">
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-white/10 flex items-center justify-center mb-4">
          <i className="ri-film-line text-3xl md:text-4xl"></i>
        </div>
        <p className="text-base md:text-lg font-medium">Aucune annonce diffusée</p>
        <p className="text-sm text-white/40 mt-1">Les vidéos ajoutées apparaîtront ici</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <video
        ref={videoRef}
        src={currentVideo ? getPublicUrl(currentVideo.storage_path) : ''}
        className={`w-full h-full object-contain transition-opacity duration-400 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        muted
        autoPlay
        playsInline
        onEnded={handleEnded}
        onError={handleError}
        preload="auto"
      />
      {/* Compteur de vidéos */}
      {activeVideos.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full">
          <i className="ri-play-list-2-line text-white/80 text-xs"></i>
          <span className="text-white/80 text-xs font-medium">
            {currentIdx + 1} / {activeVideos.length}
          </span>
          {currentVideo?.titre && (
            <>
              <span className="text-white/40 mx-1">·</span>
              <span className="text-white/60 text-xs truncate max-w-[12.5rem]">{currentVideo.titre}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ========== VUE ACCUEIL (Secrétariat) ==========
function VueAccueil({ onAjouter, salleActive, onChangerSalle, isOfflineBlocked }: {
  onAjouter: (salle: SalleType, info: PatientInfo | null, niveau: NiveauUrgence) => Promise<boolean>;
  salleActive: SalleType;
  onChangerSalle: (s: SalleType) => void;
  isOfflineBlocked: boolean;
}) {
  const [numeroDossier, setNumeroDossier] = useState('');
  const [patientTrouve, setPatientTrouve] = useState<PatientInfo | null>(null);
  const [rechercheLoading, setRechercheLoading] = useState(false);
  const [rechercheError, setRechercheError] = useState('');
  const [niveauSelectionne, setNiveauSelectionne] = useState<NiveauUrgence | null>(null);
  const [ajoutMessage, setAjoutMessage] = useState('');
  const [ajoutError, setAjoutError] = useState('');

  const rechercherPatient = async () => {
    if (!numeroDossier.trim()) { setRechercheError('Veuillez entrer un numéro de dossier'); return; }
    setRechercheLoading(true);
    setRechercheError('');
    setPatientTrouve(null);
    try {
      const { data, error: err } = await supabase
        .from('patients')
        .select('id, nom, prenom, numero_dossier, telephone')
        .ilike('numero_dossier', `%${numeroDossier.trim()}%`)
        .limit(5);
      if (err) throw err;
      if (!data || data.length === 0) {
        setRechercheError('Aucun patient trouvé avec ce numéro');
      } else {
        setPatientTrouve(data[0] as PatientInfo);
        if (data.length > 1) setRechercheError(`${data.length} patients trouvés, le premier est affiché`);
      }
    } catch {
      setRechercheError('Erreur lors de la recherche');
    } finally { setRechercheLoading(false); }
  };

  const handleAjouter = async () => {
    if (!niveauSelectionne || !patientTrouve) return;
    setAjoutError('');
    const ok = await onAjouter(salleActive, patientTrouve, niveauSelectionne);
    if (ok) {
      setAjoutMessage(`Ticket créé dans la salle ${SALLE_CONFIG[salleActive].label}`);
      setTimeout(() => setAjoutMessage(''), 3000);
      setNiveauSelectionne(null);
      setNumeroDossier('');
      setPatientTrouve(null);
    } else {
      setAjoutError('Erreur lors de l\'ajout à la file');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-lg md:text-2xl font-bold text-gray-900">Enregistrement patient</h2>
        <p className="text-gray-500 mt-1 md:mt-2 text-xs md:text-base">Recherchez un patient et ajoutez-le à la file d'attente</p>
      </div>

      {/* Sélecteur de salle */}
      <div className="bg-white rounded-2xl border-2 border-clinic-blue/10 p-4 md:p-5 mb-5 md:mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Salle de destination
        </label>
        <div className="grid grid-cols-2 gap-2 md:gap-3">
          {(Object.entries(SALLE_CONFIG) as [SalleType, typeof SALLE_CONFIG[SalleType]][]).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => onChangerSalle(key)}
              className={`p-3 md:p-4 rounded-xl border-2 transition-all cursor-pointer text-center ${
                salleActive === key
                  ? `${cfg.borderColor} ${cfg.bgLight} scale-105 shadow-sm`
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className={`text-xs md:text-sm font-bold block ${salleActive === key ? cfg.color : 'text-gray-500'}`}>
                {cfg.label}
              </span>
              <span className={`text-xs ${salleActive === key ? cfg.color : 'text-gray-400'}`}>
                Tickets aléatoires
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Recherche patient */}
      <div className="bg-white rounded-2xl border-2 border-clinic-blue/10 p-4 md:p-6 mb-5 md:mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Numéro de dossier du patient
        </label>
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
          <input
            type="text"
            value={numeroDossier}
            onChange={(e) => { setNumeroDossier(e.target.value); setPatientTrouve(null); setRechercheError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && rechercherPatient()}
            placeholder="Ex: DOS-00123"
            className="flex-1 px-4 py-3 border-2 border-clinic-blue/20 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
          />
          <button
            onClick={rechercherPatient}
            disabled={rechercheLoading}
            className="px-5 py-3 bg-clinic-blue text-white rounded-xl font-semibold hover:bg-clinic-blue-dark transition-all whitespace-nowrap cursor-pointer text-sm disabled:opacity-50"
          >
            {rechercheLoading ? 'Recherche...' : 'Rechercher'}
          </button>
        </div>

        {rechercheError && !patientTrouve && (
          <p className="mt-3 text-sm text-red-600 flex items-center gap-1">
            <i className="ri-error-warning-line"></i> {rechercheError}
          </p>
        )}

        {patientTrouve && (
          <div className="mt-4 p-3 md:p-4 bg-clinic-green/5 border-2 border-clinic-green/30 rounded-xl animate-fadeIn">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 bg-clinic-green rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-user-smile-line text-white text-lg"></i>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-gray-900 text-sm">Dossier n° {patientTrouve.numero_dossier}</p>
                <p className="text-sm text-gray-500">{patientTrouve.prenom} {patientTrouve.nom}</p>
              </div>
              {patientTrouve.telephone && (
                <p className="text-sm text-gray-500 ml-auto"><i className="ri-phone-line mr-1"></i>{patientTrouve.telephone}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Niveau d'urgence */}
      <div className="bg-white rounded-2xl border-2 border-clinic-blue/10 p-4 md:p-6 mb-5 md:mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3 md:mb-4">
          Niveau d'urgence
        </label>
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {(Object.entries(URGENCE_CONFIG) as [NiveauUrgence, typeof URGENCE_CONFIG[NiveauUrgence]][]).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setNiveauSelectionne(key)}
              className={`p-3 md:p-4 rounded-xl border-2 transition-all cursor-pointer text-center ${
                niveauSelectionne === key ? `${cfg.border} ${cfg.bg} scale-105` : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className={`text-xs md:text-sm font-bold ${niveauSelectionne === key ? cfg.text : 'text-gray-500'}`}>{cfg.label}</span>
            </button>
          ))}
        </div>
      </div>

      {ajoutMessage && (
        <div className="mb-4 p-3 md:p-4 bg-clinic-green/10 border-2 border-clinic-green/30 rounded-xl animate-fadeIn flex items-center gap-3">
          <i className="ri-checkbox-circle-fill text-clinic-green text-xl flex-shrink-0"></i>
          <span className="text-clinic-green-dark font-semibold text-sm">{ajoutMessage}</span>
        </div>
      )}
      {ajoutError && (
        <div className="mb-4 p-3 md:p-4 bg-red-50 border-2 border-red-300 rounded-xl flex items-center gap-3">
          <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0"></i>
          <span className="text-red-700 font-semibold text-sm">{ajoutError}</span>
        </div>
      )}

      <button
        onClick={handleAjouter}
        disabled={!niveauSelectionne || !patientTrouve || isOfflineBlocked}
        className={`w-full py-3 md:py-4 rounded-2xl font-bold text-white transition-all text-base md:text-lg cursor-pointer whitespace-nowrap ${
          niveauSelectionne && patientTrouve && !isOfflineBlocked
            ? 'bg-clinic-green hover:bg-clinic-green/90 shadow-lg hover:shadow-xl'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
      >
        {isOfflineBlocked ? 'Non disponible hors-ligne' : 'Ajouter à la file d\'attente'}
      </button>
    </div>
  );
}

// ========== VUE AFFICHAGE (Salle d'attente publique - style tableau aéroport) ==========
function VueAffichage({
  files,
  patientEnCoursChef,
  patientEnCoursGarde,
  videos,
  getPublicUrl,
}: {
  files: PatientFile[];
  patientEnCoursChef: PatientFile | null;
  patientEnCoursGarde: PatientFile | null;
  videos: VideoSalleAttente[];
  getPublicUrl: (path: string) => string;
}) {
  const [heure, setHeure] = useState(() => {
    const now = new Date();
    return now.toLocaleString('en-US', { timeZone: 'Africa/Porto-Novo', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  });
  const [blinkingChef, setBlinkingChef] = useState<string | null>(null);
  const [blinkingGarde, setBlinkingGarde] = useState<string | null>(null);
  const prevChefRef = useRef<string | null>(null);
  const prevGardeRef = useRef<string | null>(null);

  // Horloge temps réel — fuseau Bénin (UTC+1)
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setHeure(now.toLocaleString('en-US', { timeZone: 'Africa/Porto-Novo', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Déclencher le clignotement quand un nouveau patient est appelé
  useEffect(() => {
    const currentChefId = patientEnCoursChef?.ticket_number || null;
    if (currentChefId && currentChefId !== prevChefRef.current) {
      playAlertSound();
      setBlinkingChef(currentChefId);
      const t = setTimeout(() => setBlinkingChef(null), 3200);
      prevChefRef.current = currentChefId;
      return () => clearTimeout(t);
    }
    prevChefRef.current = currentChefId;
  }, [patientEnCoursChef?.ticket_number]);

  useEffect(() => {
    const currentGardeId = patientEnCoursGarde?.ticket_number || null;
    if (currentGardeId && currentGardeId !== prevGardeRef.current) {
      playAlertSound();
      setBlinkingGarde(currentGardeId);
      const t = setTimeout(() => setBlinkingGarde(null), 3200);
      prevGardeRef.current = currentGardeId;
      return () => clearTimeout(t);
    }
    prevGardeRef.current = currentGardeId;
  }, [patientEnCoursGarde?.ticket_number]);

  const filesAttenteChef = files
    .filter(f => f.salle === 'chef' && f.statut === 'en_attente')
    .sort((a, b) => {
      const ia = URGENCE_ORDER.indexOf(a.niveau_urgence);
      const ib = URGENCE_ORDER.indexOf(b.niveau_urgence);
      if (ia !== ib) return ia - ib;
      return a.ticket_seq - b.ticket_seq;
    })
    .slice(0, 6);

  const filesAttenteGarde = files
    .filter(f => f.salle === 'garde' && f.statut === 'en_attente')
    .sort((a, b) => {
      const ia = URGENCE_ORDER.indexOf(a.niveau_urgence);
      const ib = URGENCE_ORDER.indexOf(b.niveau_urgence);
      if (ia !== ib) return ia - ib;
      return a.ticket_seq - b.ticket_seq;
    })
    .slice(0, 6);

  // Détection intelligente : une salle ne s'affiche que si elle a des patients
  const hasChefPatients = (patientEnCoursChef !== null) || filesAttenteChef.length > 0;
  const hasGardePatients = (patientEnCoursGarde !== null) || filesAttenteGarde.length > 0;
  const bothHavePatients = hasChefPatients && hasGardePatients;

  const activeCount = videos.filter(v => v.actif).length;
  const [heures, minutes] = heure.split(':');

  // ===== Fonction de rendu d'une table =====
  const renderTable = (
    salle: SalleType,
    cfg: typeof SALLE_CONFIG[SalleType],
    enCours: PatientFile | null,
    enAttente: PatientFile[],
    blinkingId: string | null,
  ) => {
    const rows: (PatientFile & { isEnCours: boolean })[] = [];
    if (enCours) rows.push({ ...enCours, isEnCours: true });
    enAttente.forEach(f => rows.push({ ...f, isEnCours: false }));

    return (
      <div className="flex-1 min-w-0 bg-white rounded-2xl md:rounded-3xl overflow-hidden border border-gray-200">
        {/* En-tête salle */}
        <div className={`px-4 md:px-6 py-3 md:py-4 flex items-center gap-3 border-b border-gray-200 ${salle === 'chef' ? 'bg-clinic-green/5' : 'bg-clinic-blue/5'}`}>
          <div className={`w-2.5 h-2.5 rounded-full animate-pulse ${salle === 'chef' ? 'bg-clinic-green' : 'bg-clinic-blue'}`}></div>
          <h3 className={`text-sm md:text-base font-bold ${salle === 'chef' ? 'text-clinic-green' : 'text-clinic-blue'}`}>
            {cfg.label}
          </h3>
          <span className="ml-auto text-xs text-gray-500 font-mono">
            {enAttente.length + (enCours ? 1 : 0)} patient{(enAttente.length + (enCours ? 1 : 0)) > 1 ? 's' : ''}
          </span>
        </div>

        {/* Tableau */}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-100 text-min md:text-xs text-gray-500 uppercase tracking-widest">
                <th className="py-2 md:py-3 pl-4 md:pl-6 pr-2 font-semibold w-16 md:w-20">N°</th>
                <th className="py-2 md:py-3 pr-4 md:pr-6 pl-2 font-semibold text-right">Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={2} className="py-8 md:py-12 text-center text-gray-400 text-xs md:text-sm">
                    <i className="ri-inbox-line text-xl md:text-2xl block mb-2 text-gray-300"></i>
                    Aucun patient
                  </td>
                </tr>
              ) : (
                rows.map((f, idx) => {
                  const isBlinking = f.isEnCours && f.ticket_number === blinkingId;
                  const tempsEstime = idx * DUREE_CONSULTATION;

                  return (
                    <tr
                      key={f.id}
                      className={`border-b border-gray-100 transition-colors ${
                        f.isEnCours
                          ? isBlinking
                            ? 'animate-blink-appel'
                            : 'bg-clinic-green/5'
                          : idx % 2 === 0
                            ? 'bg-gray-50'
                            : ''
                      } ${f.isEnCours ? '' : 'hover:bg-gray-100'}`}
                    >
                      {/* N° Ticket */}
                      <td className="py-2.5 md:py-3.5 pl-4 md:pl-6 pr-2">
                        <span className={`font-mono text-base md:text-xl font-black ${
                          f.isEnCours
                            ? 'text-clinic-green'
                            : 'text-gray-900'
                        }`}>
                          {f.ticket_number.replace(/^[A-Z]-?/i, '')}
                        </span>
                      </td>

                      {/* Attente */}
                      <td className="py-2.5 md:py-3.5 pr-4 md:pr-6 pl-2 text-right">
                        {f.isEnCours ? (
                          isBlinking ? (
                            <span className="inline-block bg-clinic-green text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-min md:text-xs font-black animate-pulse">
                              ENTREZ SVP
                            </span>
                          ) : (
                            <span className="text-clinic-green text-xs md:text-sm font-bold">En cours</span>
                          )
                        ) : (
                          <span className="text-gray-500 text-xs md:text-sm font-mono">
                            {tempsEstime === 0 ? 'Prochain' : `~${tempsEstime} min`}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer : compteur total */}
        {files.filter(f => f.salle === salle && f.statut === 'en_attente').length > 6 && (
          <div className="px-4 md:px-6 py-2 border-t border-gray-100 text-center">
            <span className="text-min md:text-xs text-gray-400">
              +{files.filter(f => f.salle === salle && f.statut === 'en_attente').length - 6} autre{files.filter(f => f.salle === salle && f.statut === 'en_attente').length - 6 > 1 ? 's' : ''} patient{files.filter(f => f.salle === salle && f.statut === 'en_attente').length - 6 > 1 ? 's' : ''} en attente
            </span>
          </div>
        )}
      </div>
    );
  };

  // ===== AFFICHAGE PLEIN ÉCRAN : AUCUNE VIDÉO ACTIVE =====
  if (activeCount === 0) {
    return (
      <div className="bg-white rounded-2xl md:rounded-3xl overflow-hidden border border-gray-200 shadow-sm">
        {/* Barre supérieure avec horloge */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-clinic-green animate-pulse"></div>
            <span className="text-gray-600 text-xs md:text-sm font-semibold tracking-wider uppercase">Salle d'attente</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-gray-400 text-sm md:text-base"></i>
            <span className="text-gray-900 font-mono text-lg md:text-2xl font-bold tabular-nums">{heures}:{minutes}</span>
          </div>
        </div>

        {/* Tableaux : affichage intelligent — seules les salles avec des patients apparaissent */}
        {(!hasChefPatients && !hasGardePatients) ? (
          <div className="flex items-center justify-center py-20 md:py-28">
            <div className="text-center">
              <div className="w-28 h-28 md:w-36 md:h-36 flex items-center justify-center mx-auto mb-6 bg-white rounded-full p-2 border border-gray-100 shadow-sm">
                <img
                  src="https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/clinic.jpeg"
                  alt="Logo Clinique Sainte Famille"
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <h2 className="text-xl md:text-3xl font-bold text-gray-600 mb-2 tracking-tight">Bienvenue à la</h2>
              <h1 className="text-3xl md:text-5xl font-black text-clinic-green tracking-wide">Clinique Sainte Famille</h1>
              <div className="mt-6 w-16 h-0.5 bg-clinic-green/30 mx-auto rounded-full"></div>

            </div>
          </div>
        ) : (
          <div className={`flex flex-col ${bothHavePatients ? 'lg:flex-row' : ''} gap-3 md:gap-4 p-3 md:p-4 ${bothHavePatients ? '' : 'items-center'}`}>
            {hasChefPatients && (
              <div className={bothHavePatients ? 'flex-1 min-w-0' : 'w-full max-w-2xl'}>
                {renderTable('chef', SALLE_CONFIG.chef, patientEnCoursChef, filesAttenteChef, blinkingChef)}
              </div>
            )}
            {hasGardePatients && (
              <div className={bothHavePatients ? 'flex-1 min-w-0' : 'w-full max-w-2xl'}>
                {renderTable('garde', SALLE_CONFIG.garde, patientEnCoursGarde, filesAttenteGarde, blinkingGarde)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ===== AFFICHAGE SPLIT : VIDÉOS ACTIVES =====
  return (
    <div className="flex flex-col lg:flex-row gap-4 md:gap-6 min-h-[60vh]">
      {/* Zone vidéo */}
      <div className="flex-1 bg-[#06121c] rounded-2xl md:rounded-3xl overflow-hidden relative min-h-[300px] md:min-h-[450px]">
        <VideoPlayer videos={videos} getPublicUrl={getPublicUrl} />
      </div>

      {/* Sidebar avec horloge + tableaux compacts */}
      <div className="lg:w-[26.25rem] xl:w-[30rem] flex-shrink-0 space-y-3 md:space-y-4 overflow-y-auto max-h-[70vh] lg:max-h-none">
        {/* Horloge + compteur vidéos */}
        <div className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 border border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-clinic-green animate-pulse"></div>
            <span className="text-gray-600 text-xs font-semibold">Salle d'attente</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-xs">
              <i className="ri-film-line mr-1"></i>{activeCount} vidéo{activeCount > 1 ? 's' : ''}
            </span>
            <span className="text-gray-900 font-mono text-base font-bold">{heures}:{minutes}</span>
          </div>
        </div>

        {/* Tableaux compacts — affichage intelligent */}
        {(!hasChefPatients && !hasGardePatients) ? (
          <div className="flex items-center justify-center py-14 md:py-16">
            <div className="text-center">
              <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center mx-auto mb-4 bg-white rounded-full p-1.5 border border-gray-100 shadow-sm">
                <img
                  src="https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/clinic.jpeg"
                  alt="Logo Clinique Sainte Famille"
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <h3 className="text-base md:text-lg font-bold text-gray-600 mb-1">Bienvenue à la</h3>
              <h2 className="text-xl md:text-2xl font-black text-clinic-green">Clinique Sainte Famille</h2>
              <div className="mt-4 w-12 h-px bg-clinic-green/30 mx-auto rounded-full"></div>
            </div>
          </div>
        ) : (
          <>
            {hasChefPatients && renderTable('chef', SALLE_CONFIG.chef, patientEnCoursChef, filesAttenteChef, blinkingChef)}
            {hasGardePatients && renderTable('garde', SALLE_CONFIG.garde, patientEnCoursGarde, filesAttenteGarde, blinkingGarde)}
          </>
        )}
      </div>
    </div>
  );
}

// ========== VUE MÉDECIN ==========
function VueMedecin({
  salle,
  patientEnCours,
  filesEnAttente,
  onAppelerSuivant,
  onTerminerConsultation,
  isOfflineBlocked,
}: {
  salle: SalleType;
  patientEnCours: PatientFile | null;
  filesEnAttente: PatientFile[];
  onAppelerSuivant: (salle: SalleType) => Promise<string | null>;
  onTerminerConsultation: (salle: SalleType) => Promise<boolean>;
  isOfflineBlocked: boolean;
}) {
  const [appelMessage, setAppelMessage] = useState('');
  const [terminerLoading, setTerminerLoading] = useState(false);
  const cfg = SALLE_CONFIG[salle];

  const handleSuivant = async () => {
    const ticket = await onAppelerSuivant(salle);
    if (ticket) {
      setAppelMessage(`Ticket ${ticket} appelé !`);
      playAlertSound();
    } else {
      setAppelMessage('Aucun patient en attente');
    }
    setTimeout(() => setAppelMessage(''), 4000);
  };

  const handleTerminer = async () => {
    setTerminerLoading(true);
    const ok = await onTerminerConsultation(salle);
    if (ok) {
      setAppelMessage('Consultation terminée — salle libérée !');
    } else {
      setAppelMessage('Erreur lors de la fermeture');
    }
    setTerminerLoading(false);
    setTimeout(() => setAppelMessage(''), 4000);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-5 md:mb-6">
        <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold ${cfg.bgLight} ${cfg.color}`}>
          {cfg.label}
        </span>
      </div>

      {/* Patient en cours */}
      <div className="mb-6 md:mb-8">
        <h3 className="text-xs md:text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3 md:mb-4 text-center">Consultation en cours</h3>
        {patientEnCours ? (
          <div className="bg-white rounded-2xl md:rounded-3xl border-2 border-clinic-blue/20 p-5 md:p-8 text-center shadow-lg">
            <div className="w-20 h-20 md:w-28 md:h-28 bg-clinic-blue rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
              <span className="text-2xl md:text-4xl font-black text-white">{patientEnCours.ticket_number}</span>
            </div>
            <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${URGENCE_CONFIG[patientEnCours.niveau_urgence].text} ${URGENCE_CONFIG[patientEnCours.niveau_urgence].bg}`}>
              {URGENCE_CONFIG[patientEnCours.niveau_urgence].label}
            </span>
            {patientEnCours.numero_dossier && (
              <p className="text-xs md:text-sm text-gray-500 mt-2">Dossier n° {patientEnCours.numero_dossier}</p>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-2xl md:rounded-3xl p-8 md:p-10 text-center border-2 border-dashed border-gray-300">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
              <i className="ri-user-line text-2xl md:text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-base md:text-lg">Aucune consultation en cours</p>
          </div>
        )}
      </div>

      {/* Boutons d'action */}
      <div className="mb-6 md:mb-8">
        {/* Cas 1 : Patient en cours + patients en attente → bouton Suivant */}
        {filesEnAttente.length > 0 && (
          <button
            onClick={handleSuivant}
            disabled={isOfflineBlocked}
            className={`w-full py-4 md:py-5 rounded-2xl font-bold text-white transition-all text-lg md:text-xl cursor-pointer whitespace-nowrap ${
              isOfflineBlocked
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-clinic-green hover:bg-clinic-green/90 shadow-lg hover:shadow-xl'
            }`}
          >
            {isOfflineBlocked ? 'Non disponible hors-ligne' : 'Appeler le patient suivant'}
          </button>
        )}

        {/* Cas 2 : Patient en cours + AUCUN patient en attente → bouton Terminer */}
        {filesEnAttente.length === 0 && patientEnCours && (
          <button
            onClick={handleTerminer}
            disabled={terminerLoading || isOfflineBlocked}
            className={`w-full py-4 md:py-5 rounded-2xl font-bold text-white transition-all text-lg md:text-xl cursor-pointer whitespace-nowrap ${
              isOfflineBlocked
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-red-500 hover:bg-red-600 shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-wait'
            }`}
          >
            {isOfflineBlocked ? 'Non disponible hors-ligne' : terminerLoading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Fermeture...
              </span>
            ) : (
              'Terminer la consultation'
            )}
          </button>
        )}

        {/* Cas 3 : Aucun patient du tout → bouton grisé */}
        {filesEnAttente.length === 0 && !patientEnCours && (
          <button
            disabled
            className="w-full py-4 md:py-5 rounded-2xl font-bold text-white transition-all text-lg md:text-xl cursor-not-allowed whitespace-nowrap bg-gray-300"
          >
            Aucun patient
          </button>
        )}

        {appelMessage && (
          <p className={`mt-3 text-center font-semibold animate-fadeIn text-sm ${
            appelMessage.includes('terminée') || appelMessage.includes('libérée') ? 'text-clinic-green' :
            appelMessage.includes('Erreur') ? 'text-red-500' : 'text-clinic-green'
          }`}>{appelMessage}</p>
        )}
      </div>

      {/* Prochains patients */}
      <div>
        <h3 className="text-xs md:text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3 md:mb-4">
          Prochains patients ({filesEnAttente.length})
        </h3>
        {filesEnAttente.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">
            Aucun patient en attente
          </div>
        ) : (
          <div className="space-y-2">
            {filesEnAttente.slice(0, 10).map((patient, idx) => {
              const ucfg = URGENCE_CONFIG[patient.niveau_urgence];
              const tempsEstime = (idx + (patientEnCours ? 1 : 0)) * DUREE_CONSULTATION;
              return (
                <div key={patient.id} className="flex items-center gap-3 p-2.5 md:p-3 rounded-xl bg-white border border-gray-100">
                  <span className={`w-10 h-10 md:w-11 md:h-11 ${ucfg.badge} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <span className="text-xs font-black text-white">{patient.ticket_number}</span>
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs font-semibold ${ucfg.text}`}>{ucfg.label}</span>
                    {patient.numero_dossier && (
                      <span className="text-xs text-gray-400 ml-2 truncate">Dossier {patient.numero_dossier}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">~{tempsEstime} min</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ========== VUE MÉDIAS (Gestion des vidéos) ==========
function VueMedias({
  videos,
  loading,
  error,
  uploadVideo,
  toggleActif,
  deleteVideo,
  moveVideo,
  getPublicUrl,
}: {
  videos: VideoSalleAttente[];
  loading: boolean;
  error: string;
  uploadVideo: (file: File, titre: string) => Promise<boolean>;
  toggleActif: (id: string, actif: boolean) => void;
  deleteVideo: (video: VideoSalleAttente) => void;
  moveVideo: (id: string, direction: 'up' | 'down', allVideos: VideoSalleAttente[]) => void;
  getPublicUrl: (path: string) => string;
}) {
  const [titre, setTitre] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Format non supporté. Utilisez MP4, WebM, OGG ou MOV.');
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      setUploadError(`Fichier trop volumineux (max ${Math.round(MAX_VIDEO_SIZE / 1024 / 1024)} Mo)`);
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadMessage('');

    const success = await uploadVideo(file, titre.trim());
    if (success) {
      setUploadMessage('Vidéo uploadée avec succès !');
      setTitre('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => setUploadMessage(''), 3000);
    }
    setUploading(false);
  };

  const handleDelete = async (video: VideoSalleAttente) => {
    setDeleteConfirm(null);
    deleteVideo(video);
  };

  const sorted = [...videos].sort((a, b) => a.ordre - b.ordre);
  const activeCount = videos.filter(v => v.actif).length;

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto text-center py-10">
        <i className="ri-loader-4-line text-3xl text-clinic-blue animate-spin mb-4 block"></i>
        <p className="text-gray-500">Chargement des médias...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-lg md:text-2xl font-bold text-gray-900">Gestion des annonces</h2>
        <p className="text-gray-500 mt-1 md:mt-2 text-xs md:text-base">
          Uploader des vidéos diffusées dans la salle d'attente
        </p>
        {activeCount > 0 && (
          <p className="text-clinic-blue text-sm font-medium mt-2">
            {activeCount} vidéo{activeCount > 1 ? 's' : ''} active{activeCount > 1 ? 's' : ''} en diffusion
          </p>
        )}
      </div>

      {/* Upload zone */}
      <div className="bg-white rounded-2xl border-2 border-dashed border-clinic-blue/20 p-5 md:p-8 mb-5 md:mb-6 text-center">
        <div className="w-14 h-14 md:w-16 md:h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
          <i className="ri-upload-cloud-2-line text-2xl md:text-3xl text-clinic-blue"></i>
        </div>
        <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">Ajouter une vidéo</h3>
        <p className="text-xs md:text-sm text-gray-500 mb-4">
          Formats acceptés : MP4, WebM, OGG, MOV — Max 100 Mo
        </p>

        <div className="max-w-md mx-auto space-y-3">
          <input
            type="text"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            placeholder="Titre de la vidéo (optionnel)"
            className="w-full px-4 py-2.5 border-2 border-clinic-blue/20 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/webm,video/ogg,video/quicktime"
            onChange={handleFileSelect}
            className="hidden"
            id="video-upload-input"
          />
          <label
            htmlFor="video-upload-input"
            className={`block w-full py-3 rounded-xl font-semibold text-white transition-all cursor-pointer text-sm whitespace-nowrap ${
              uploading
                ? 'bg-gray-400 cursor-wait'
                : 'bg-clinic-blue hover:bg-clinic-blue-dark'
            }`}
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Upload en cours...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-video-add-line"></i>
                Choisir une vidéo
              </span>
            )}
          </label>
        </div>

        {uploadMessage && (
          <div className="mt-4 p-3 bg-clinic-green/10 border-2 border-clinic-green/30 rounded-xl animate-fadeIn flex items-center gap-3 max-w-md mx-auto">
            <i className="ri-checkbox-circle-fill text-clinic-green text-xl flex-shrink-0"></i>
            <span className="text-clinic-green-dark font-semibold text-sm">{uploadMessage}</span>
          </div>
        )}
        {uploadError && (
          <div className="mt-4 p-3 bg-red-50 border-2 border-red-300 rounded-xl flex items-center gap-3 max-w-md mx-auto">
            <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0"></i>
            <span className="text-red-700 font-semibold text-sm">{uploadError}</span>
          </div>
        )}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border-2 border-red-300 rounded-xl flex items-center gap-3 max-w-md mx-auto">
            <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0"></i>
            <span className="text-red-700 font-semibold text-sm">{error}</span>
          </div>
        )}
      </div>

      {/* Liste des vidéos */}
      <div className="bg-white rounded-2xl border-2 border-clinic-blue/10 p-4 md:p-6">
        <h3 className="text-sm font-bold text-gray-700 mb-4">
          Vidéos uploadées ({videos.length})
        </h3>

        {sorted.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <i className="ri-film-line text-3xl mb-2 block"></i>
            <p className="text-sm">Aucune vidéo uploadée</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sorted.map((video, idx) => (
              <div
                key={video.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  video.actif
                    ? 'border-clinic-green/30 bg-clinic-green/5'
                    : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => moveVideo(video.id, 'up', sorted)}
                    disabled={idx === 0}
                    className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
                  >
                    <i className="ri-arrow-up-s-line text-gray-500 text-sm"></i>
                  </button>
                  <button
                    onClick={() => moveVideo(video.id, 'down', sorted)}
                    disabled={idx === sorted.length - 1}
                    className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
                  >
                    <i className="ri-arrow-down-s-line text-gray-500 text-sm"></i>
                  </button>
                </div>

                {/* Video preview thumbnail */}
                <div className="w-16 h-10 md:w-24 md:h-14 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                  <video
                    src={getPublicUrl(video.storage_path)}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {video.titre || 'Sans titre'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(video.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>

                {/* Toggle actif */}
                <button
                  onClick={() => toggleActif(video.id, !video.actif)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    video.actif
                      ? 'bg-clinic-green/10 text-clinic-green border border-clinic-green/30'
                      : 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
                  }`}
                  title={video.actif ? 'Désactiver' : 'Activer'}
                >
                  {video.actif ? 'Actif' : 'Inactif'}
                </button>

                {/* Delete */}
                {deleteConfirm === video.id ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleDelete(video)}
                      className="px-2 py-1.5 bg-red-500 text-white rounded-lg text-xs font-semibold cursor-pointer hover:bg-red-600 transition-all whitespace-nowrap"
                    >
                      Confirmer
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="px-2 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-xs cursor-pointer hover:bg-gray-300 transition-all whitespace-nowrap"
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(video.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all cursor-pointer flex-shrink-0"
                    title="Supprimer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ========== PAGE PRINCIPALE ==========
export default function FileAttentePage() {
  const { role } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const salleFromUrl = searchParams.get('salle') as SalleType | null;
  const vueFromUrl = searchParams.get('vue') as TabVue | null;

  // Rôles et leurs tabs autorisés
  const roleTabs: Record<string, TabVue[]> = {
    super_admin: ['accueil', 'affichage', 'medecin', 'medias'],
    medecin: ['accueil', 'medecin'],
    secretaire: ['accueil', 'affichage'],
  };
  const allowedTabs = roleTabs[role || ''] || ['accueil'];

  const [salleActive, setSalleActive] = useState<SalleType>(salleFromUrl === 'chef' ? 'chef' : salleFromUrl === 'garde' ? 'garde' : 'chef');
  const [activeTab, setActiveTab] = useState<TabVue>(() => {
    if (vueFromUrl && allowedTabs.includes(vueFromUrl)) return vueFromUrl;
    return allowedTabs[0];
  });
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  const { files, loading, error, ajouterPatient, appelerSuivant, terminerConsultation, retryLoad, getPatientEnCours, getFilesEnAttente } = useFileAttenteRealtime();
  const videoHook = useVideosSalleAttente();
  const offlineGuard = useOfflineGuard('file_attente', 'write');

  // Timeout de sécurité : si le chargement prend plus de 12s, afficher un message
  useEffect(() => {
    if (!loading) {
      setLoadingTimeout(false);
      return;
    }
    const timer = setTimeout(() => setLoadingTimeout(true), 12000);
    return () => clearTimeout(timer);
  }, [loading]);

  // Forcer la vue et la salle si venant de la page médecin
  useEffect(() => {
    if (vueFromUrl === 'medecin' && allowedTabs.includes('medecin')) {
      setActiveTab('medecin');
      if (salleFromUrl) setSalleActive(salleFromUrl);
    }
  }, [vueFromUrl, salleFromUrl, allowedTabs]);

  const patientEnCoursChef = getPatientEnCours('chef');
  const patientEnCoursGarde = getPatientEnCours('garde');

  const updateParams = (tab: TabVue, salle?: SalleType) => {
    const params = new URLSearchParams();
    params.set('vue', tab);
    if (salle) params.set('salle', salle);
    setSearchParams(params, { replace: true });
  };

  const handleTabChange = (tab: TabVue) => {
    setActiveTab(tab);
    updateParams(tab, salleActive);
  };

  const handleSalleChange = (salle: SalleType) => {
    setSalleActive(salle);
    updateParams(activeTab, salle);
  };

  const allTabs: { key: TabVue; label: string; shortLabel: string }[] = [
    { key: 'accueil', label: 'Accueil', shortLabel: 'Accueil' },
    { key: 'affichage', label: 'Salle d\'attente', shortLabel: 'Salle' },
    { key: 'medecin', label: 'Médecin', shortLabel: 'Médecin' },
    { key: 'medias', label: 'Médias', shortLabel: 'Médias' },
  ];

  const tabs = allTabs.filter(t => allowedTabs.includes(t.key));

  // URL plein écran avec basePath
  const getFullscreenUrl = () => {
    const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
    const pathPrefix = basePath ? `/${basePath}` : '';
    return `${pathPrefix}/file-attente?vue=affichage&fullscreen=1`;
  };

  // Détection mode plein écran (juste la salle d'attente, sans le reste de la page)
  const isFullscreen = vueFromUrl === 'affichage' && searchParams.get('fullscreen') === '1';

  if (loading && !loadingTimeout) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-40">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4 block"></i>
            <p className="text-gray-500">Chargement de la file d'attente...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading && loadingTimeout) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-40">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-wifi-off-line text-3xl text-amber-600"></i>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Connexion au serveur lente</h2>
            <p className="text-gray-500 text-sm mb-6">
              Le chargement prend plus de temps que prévu. Cela peut être dû à une connexion internet instable ou à un problème temporaire du serveur.
            </p>
            <button
              onClick={retryLoad}
              className="px-6 py-3 bg-clinic-blue text-white rounded-xl font-semibold hover:bg-clinic-blue-dark transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-refresh-line mr-2"></i>Réessayer
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ===== MODE PLEIN ÉCRAN : uniquement le tableau d'affichage =====
  if (isFullscreen) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-2 md:p-4">
        <div className="w-full max-w-[100rem]">
          <VueAffichage
            files={files}
            patientEnCoursChef={patientEnCoursChef}
            patientEnCoursGarde={patientEnCoursGarde}
            videos={videoHook.videos}
            getPublicUrl={videoHook.getPublicUrl}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Bannière */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-gray-900">File d'attente</h1>
              <p className="mt-1 text-gray-500 text-xs md:text-base">Gestion des patients en salle d'attente</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                const chefAttente = files.filter(f => f.salle === 'chef' && f.statut === 'en_attente').length;
                const chefEnCours = files.filter(f => f.salle === 'chef' && f.statut === 'en_cours').length;
                const chefTotal = chefAttente + chefEnCours;
                if (chefTotal > 0) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-clinic-green/5 rounded-full border border-clinic-green/30">
                      <span className="text-xs font-semibold text-clinic-green whitespace-nowrap">
                        Dr OLYMPIO: {chefAttente} att. + {chefEnCours} en cours
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              {(() => {
                const gardeAttente = files.filter(f => f.salle === 'garde' && f.statut === 'en_attente').length;
                const gardeEnCours = files.filter(f => f.salle === 'garde' && f.statut === 'en_cours').length;
                const gardeTotal = gardeAttente + gardeEnCours;
                if (gardeTotal > 0) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-clinic-blue/5 rounded-full border border-clinic-blue/30">
                      <span className="text-xs font-semibold text-clinic-blue whitespace-nowrap">
                        Garde: {gardeAttente} att. + {gardeEnCours} en cours
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              {error && (
                <button onClick={retryLoad} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-xs font-semibold cursor-pointer hover:bg-red-100 whitespace-nowrap">
                  <i className="ri-refresh-line mr-1"></i>Réessayer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6">
        <div className="flex bg-white rounded-2xl p-1 md:p-1.5 border-2 border-clinic-blue/10 shadow-sm overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`flex-1 py-2.5 md:py-3 px-2 md:px-4 rounded-xl font-semibold text-xs md:text-base transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-clinic-blue text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.shortLabel}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8">
        {/* Offline warning banner for write operations */}
        {offlineGuard.isBlocked && (activeTab === 'accueil' || activeTab === 'medecin') && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 bg-amber-50 border-2 border-amber-300 rounded-xl flex items-start gap-3 animate-fadeIn">
            <div className="w-8 h-8 md:w-9 md:h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-wifi-off-line text-amber-600 text-base md:text-lg"></i>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800">Fonctionnalité non disponible hors-ligne</p>
              <p className="text-xs md:text-sm text-amber-700 mt-1">{offlineGuard.blockMessage || 'La modification de la file d\'attente nécessite une connexion internet.'}</p>
            </div>
          </div>
        )}
        {activeTab === 'accueil' && (
          <VueAccueil onAjouter={ajouterPatient} salleActive={salleActive} onChangerSalle={handleSalleChange} isOfflineBlocked={offlineGuard.isBlocked} />
        )}
        {activeTab === 'affichage' && !isFullscreen && (
          <>
            <div className="flex justify-end mb-3 md:mb-4">
              <button
                onClick={() => window.open(getFullscreenUrl(), '_blank', 'width=1200,height=800')}
                className="px-3 md:px-4 py-2 bg-white border-2 border-clinic-blue/20 rounded-full text-xs md:text-sm font-semibold text-clinic-blue hover:bg-clinic-blue/5 transition-all cursor-pointer whitespace-nowrap"
              >
                Ouvrir en plein écran
              </button>
            </div>
            <VueAffichage
              files={files}
              patientEnCoursChef={patientEnCoursChef}
              patientEnCoursGarde={patientEnCoursGarde}
              videos={videoHook.videos}
              getPublicUrl={videoHook.getPublicUrl}
            />
          </>
        )}
        {activeTab === 'medecin' && (
          <VueMedecin
            salle={salleActive}
            patientEnCours={salleActive === 'chef' ? patientEnCoursChef : patientEnCoursGarde}
            filesEnAttente={files.filter(f => f.salle === salleActive && f.statut === 'en_attente')}
            onAppelerSuivant={appelerSuivant}
            onTerminerConsultation={terminerConsultation}
            isOfflineBlocked={offlineGuard.isBlocked}
          />
        )}
        {activeTab === 'medias' && (
          <VueMedias
            videos={videoHook.videos}
            loading={videoHook.loading}
            error={videoHook.error}
            uploadVideo={videoHook.uploadVideo}
            toggleActif={videoHook.toggleActif}
            deleteVideo={videoHook.deleteVideo}
            moveVideo={videoHook.moveVideo}
            getPublicUrl={videoHook.getPublicUrl}
          />
        )}
      </div>

      <Footer />
    </div>
  );
}